import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useSaveJobPostDetail } from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';
import { IJobDetailsForm, JobDetailsFormMapper } from '../../JobDetailsForm';
import {
  CreatePermanentJobPostingSteps,
  TCreatePermanentJobPostingPageLocationState,
} from '../Types';

/**
 * Fields validated before continuing from Confirm Job Details to Review & Payment.
 *
 * Mirrors the required fields the manual flow enforces across its steps, minus:
 * - `office` — associated server-side on draft creation (the manual create path
 *   likewise does not block on it);
 * - the optional fields (`expectedDays`, `benefits`, `benefitsOther`).
 */
const CONFIRM_JOB_DETAILS_TRIGGERS: (keyof IJobDetailsForm)[] = [
  'jobTitle',
  'professionType',
  'officeSpecialty',
  'scheduleType',
  'payType',
  'compensationType',
  'minRate',
  'maxRate',
  'jobDescription',
];

/**
 * Drives the single-page Confirm Job Details review screen of the Copy & Paste
 * (import job description) flow.
 *
 * The draft already exists — it was created and pre-filled with the parsed
 * fields when the office clicked "Add". On "Next" this validates the whole
 * composed form, persists the reviewed values onto that same draft, and routes
 * into the shared Review & Payment step (flagged `fromImport` so the stepper and
 * back navigation match the import flow).
 */
export const useConfirmJobDetails = (
  controller: UseFormReturn<IJobDetailsForm>,
  jobPostingId: string | undefined,
) => {
  const navigate = useNavigate();
  const { isPending, mutate } = useSaveJobPostDetail();

  const hasFormErrors = CONFIRM_JOB_DETAILS_TRIGGERS.some(
    (field) => !!controller.getFieldState(field).error,
  );

  const onNext = useCallback(async () => {
    if (!jobPostingId) {
      return;
    }

    const isValid = await controller.trigger(CONFIRM_JOB_DETAILS_TRIGGERS);

    if (!isValid) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    mutate(
      {
        id: jobPostingId,
        payload: JobDetailsFormMapper.toSaveJobPostDetailPayload(controller.getValues()),
      },
      {
        onSuccess: () => {
          const state: TCreatePermanentJobPostingPageLocationState = { fromImport: true };
          navigate(
            // eslint-disable-next-line max-len
            `/permanent-jobs/postings/${jobPostingId}/create/${CreatePermanentJobPostingSteps.ReviewAndPayment}`,
            { state },
          );
        },
        onError: (err) => showErrorMessage({ error: err }),
      },
    );
  }, [controller, jobPostingId, mutate, navigate]);

  return { onNext, isPending, hasFormErrors };
};
